/**
 * Mock-quiz unit renderer.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";

import ProblemMock from "pages/board/problems/ProblemMock";
import QuizFailure from "./QuizFailure";
import QuizSuccess from "./QuizSuccess";
import QuizRetry from "./QuizRetry";
import QuizFirst from "./QuizFirst";
import { getCorrectProblemBlockCount, getGradeCutoff, getGradeForBlock } from "utils/grade.util";
import { getSectionTitle } from "utils/course";
import { actionCreateCertificate } from "context/action";
import { useAsterController } from "context";
import { getBrighterColor } from "utils/string";

/**
 * Orchestrates quiz intro, attempt, retry, success, and failure screens.
 *
 * @param {{ xblocks?: Array, onSubmitProblem?: Function }} props
 */
const MockExamModuleRender = (props) => {
  const { xblocks, onSubmitProblem, onReSubmitProblems, setStackedAnswers } = props;

  const { t } = useTranslation("common");

  const { courseId, sectionIndex } = useParams();

  const [controller, dispatch] = useAsterController();
  const { courses, structure: courseStructure, themes } = controller;

  const colorMain1 = (themes || []).find((v) => v.name === "color_main1")?.value || "#F76060";

  const [cursor, setCursor] = useState(-1);
  const [problemBlocks, setProblemBlocks] = useState([]);
  const [originCorrectCount, setOriginCorrectCount] = useState(0);
  const [badgeEarned, setBadgeEarned] = useState(false);
  const [retryBadgeEarned, setRetryBadgeEarned] = useState(false);
  const currCourse = useMemo(() => (courses.data || []).find((v) => v.id === courseId) || {}, [courses.data, courseId]);

  /**
   * Counts fully correct problem blocks and updates badge eligibility.
   *
   * @returns {number}
   */
  const setBadgeStatus = useCallback(() => {
    const correctCount = getCorrectProblemBlockCount(
      currCourse,
      problemBlocks.map((p) => p.id)
    )["counter"];
    let cutoff = getGradeCutoff(currCourse);
    cutoff = cutoff === 0 ? 1 : cutoff;
    const total = problemBlocks.length;

    if (total * cutoff < correctCount) {
      // If this is the first time to pass this module, we register this module
      if (cursor + 1 === problemBlocks.length && !badgeEarned) {
        actionCreateCertificate(dispatch, {
          course_id: courseId,
          course_title: currCourse.display_name,
          module_title: getSectionTitle(courseStructure, sectionIndex),
          is_final: false,
          total: currCourse.moduleMapper ? currCourse.moduleMapper.filter((v) => v.gradeFormat && v.gradeFormat.toLowerCase() === "quiz").length : 0,
          is_save: true,
        });
      }

      setBadgeEarned(true);
      setOriginCorrectCount(correctCount);
      setRetryBadgeEarned(true);
      setCursor(total);
    } else {
      setRetryBadgeEarned(false);
    }

    return correctCount;
  }, [badgeEarned, courseId, courseStructure, currCourse, cursor, dispatch, problemBlocks, sectionIndex]);

  // Set the badge earned state
  useEffect(() => (courses.data || []).length && problemBlocks.length && cursor === -1 && setOriginCorrectCount(setBadgeStatus()), [courses, problemBlocks, cursor, setBadgeStatus]);
  useEffect(() => setProblemBlocks(xblocks.filter((b) => b.type === "problem").sort(() => 0.5 - Math.random())), [xblocks]);

  // If continue button clicked
  /**
   * Advances the quiz flow after checking current scores.
   *
   * @returns {void}
   */
  const onContinueClick = () => {
    // If the next page if final evaluation page
    if (cursor + 1 === problemBlocks.length) {
      const currCorrectCount = setBadgeStatus();

      // If retry grade is above than origin grade, then submit this time answers to DB
      currCorrectCount > originCorrectCount && badgeEarned && onReSubmitProblems();
    }
    setCursor(cursor + 1);
  };

  /**
   * Restarts the mock quiz from the first screen.
   *
   * @returns {void}
   */
  const onRestartClick = () => {
    setCursor(0);
    setStackedAnswers([]);
    setRetryBadgeEarned(false);
    setProblemBlocks(xblocks.filter((b) => b.type === "problem").sort(() => 0.5 - Math.random()));
  };

  /**
   * Forwards a mock-quiz answer, skipping persist when the badge is already earned.
   *
   * @param {Object} payload Answer payload.
   * @returns {void}
   */
  const onMockProblemSubmit = (payload) => onSubmitProblem(payload, badgeEarned ? "NOT_ALLOW_POST" : "ALLOW_POST");

  let correctedNumber = getCorrectProblemBlockCount(
    currCourse,
    problemBlocks.map((p) => p.id)
  )["counter"];

  return (
    <>
      {currCourse && (
        <>
          {cursor === -1 && <QuizFirst totalCount={problemBlocks.length} onContinueClick={onContinueClick} cutoff={getGradeCutoff(currCourse)} />}
          {0 <= cursor && cursor < problemBlocks.length && (
            <>
              <Box className="text-xs mt-4 text-uppercase">
                {t("Question")}&nbsp;
                {cursor + 1}/{problemBlocks.length}
              </Box>
              <Box className="flex mt-2 bg-gray-200 w-full rounded mb-8" style={{ height: 10 }}>
                <Box style={{ width: `${((cursor + 1) * 100) / problemBlocks.length}%`, backgroundImage: `linear-gradient(to right, ${getBrighterColor(colorMain1, 50)}, ${colorMain1})` }} className="rounded"></Box>
              </Box>
              <ProblemMock key={cursor} rawText={problemBlocks[cursor].html} grade={getGradeForBlock(currCourse, problemBlocks[cursor].id)} courseId={courseStructure.course_id} blockId={problemBlocks[cursor].id} onSubmitProblem={onMockProblemSubmit} onContinueClick={onContinueClick} />
            </>
          )}
          {cursor === problemBlocks.length && badgeEarned && retryBadgeEarned && <QuizSuccess onRestartClick={onRestartClick} />}
          {cursor === problemBlocks.length && badgeEarned && !retryBadgeEarned && <QuizRetry onRestartClick={onRestartClick} totalCount={problemBlocks.length} successCount={correctedNumber} />}
          {cursor === problemBlocks.length && !badgeEarned && <QuizFailure onRestartClick={onRestartClick} totalCount={problemBlocks.length} successCount={correctedNumber} />}
        </>
      )}
    </>
  );
};

export default MockExamModuleRender;
